import _ from 'lodash';
import {
  ChangesetReason,
  Design,
  DesignChangeMap,
  DesignRootSettingsChangeItem
} from '../../../types';
import { mergeDesignObjectModification } from './merge-design-object-modification';
import { defaultServiceContext } from '../../../contexts';

export interface ApplyChangeMapProps {
  /** Change map to apply to @see design. */
  changeMap: DesignChangeMap;
  /** Design to apply @see changeMap to. */
  design: Design;
}

/** Used to quickly access values off from change map settings. */
type ChangeMapSetting<
  K extends keyof(Required<DesignChangeMap>['settings'])
> = Required<Required<DesignChangeMap>['settings']>[K];

export function applyChangeMap({
  changeMap,
  design
}: ApplyChangeMapProps): Design {
  design = _.cloneDeep(design);
  const errors: string[] = [];
  const { errorMessageService } = defaultServiceContext;

  // Merge application modules.
  Object.entries(
    changeMap['application-modules'] ?? {}
  ).forEach(([key, changeset]) => {
    switch (changeset.reason) {
      case ChangesetReason.added:
        design.components.modules.push(changeset.node);
        break;
      case ChangesetReason.modified:
        design.components.modules.forEach((mod) => {
          if (mod.id !== key) {
            return;
          }

          const { localVariables, versions } = mod;
          Object.assign(mod, changeset.node);
          // Do not update localVariables or versions because those are stored under DesignChangeMap.modules.
          mod.localVariables = localVariables;
          mod.versions = versions;
        });
        break;
      case ChangesetReason.deleted:
        design.components.modules = design.components.modules.filter((mod) => {
          return mod.id !== key;
        });
        break;
    }
  });

  // Merge application variables.
  Object.entries(
    changeMap['application-variables'] ?? {}
  ).forEach(([key, changeset]) => {
    switch (changeset.reason) {
      case ChangesetReason.added:
        design.components.variables.push(changeset.node);
        break;
      case ChangesetReason.modified:
        design.components.variables = design.components.variables.map((v) => {
          return (v.id === key) ? changeset.node : v;
        });
        break;
      case ChangesetReason.deleted:
        design.components.variables = design.components.variables.filter((v) => {
          return v.id !== key;
        });
        break;
    }
  });

  // Merge application objects.
  Object.entries(
    changeMap['application-objects'] ?? {}
  ).forEach(([key, changeset]) => {
    switch (changeset.reason) {
      case ChangesetReason.added:
        design.components.objects.push(changeset.node);
        break;
      case ChangesetReason.modified:
        design.components.objects = design.components.objects.map((obj) => {
          if (obj.id === key) {
            return mergeDesignObjectModification(changeset.node, obj);
          } else {
            return obj;
          }
        });
        break;
      case ChangesetReason.deleted:
        design.components.objects = design.components.objects.filter((obj) => {
          return obj.id !== key;
        });
        break;
    }
  });

  // Merge application intents.
  Object.entries(
    changeMap['application-intents'] ?? {}
  ).forEach(([key, changeset]) => {
    switch (changeset.reason) {
      case ChangesetReason.added:
        design.components.intents.push(changeset.node);
        break;
      case ChangesetReason.modified:
        design.components.intents = design.components.intents.map((intent) => {
          return (intent.id === key) ? changeset.node : intent;
        });
        break;
      case ChangesetReason.deleted:
        design.components.intents = design.components.intents.filter((int) => {
          return int.id !== key;
        });
        break;
    }
  });

  if (changeMap.applicationVersion) {
    const changeset = changeMap.applicationVersion;
    design.applicationVersion = changeset.object;
    design.applicationVersionLastChange = (changeset.lastChange) ? (
      changeset.lastChange
    ) : design.applicationVersionLastChange;
  }

  // Merge modules and their content.
  Object.entries(
    changeMap.modules ?? {}
  ).forEach(([key, moduleChangeset]) => {
    const appModule = design.components.modules.find((mod) => {
      return mod.id === key;
    });

    if (!appModule) {
      errors.push(`Unable to apply changes to module ${key}.`);
      return;
    }

    const appModuleFlow = appModule.versions[1].flow;

    // Merge module nodes.
    (moduleChangeset.nodes || []).forEach((changeset) => {
      const appNodes = appModuleFlow.nodes;
      const node = changeset.node;
      const index = appNodes.findIndex((n) => {
        return n.id === node.id;
      });

      switch (changeset.reason) {
        case ChangesetReason.modified:
          if (index !== -1) {
            appNodes[index] = mergeDesignObjectModification(node, appNodes[index]);
          }
          break;
        case ChangesetReason.added:
          if (index === -1) {
            appNodes.push(node);
          }
          break;
        case ChangesetReason.deleted:
          if (index !== -1) {
            appNodes.splice(index, 1);
          }
          break;
      }
    });

    // Merge module links.
    (moduleChangeset.links || []).forEach((changeset) => {
      const appLinks = appModuleFlow.links;
      const link = changeset.link;
      const index = appLinks.findIndex((l) => {
        return l.id === link.id;
      });

      switch (changeset.reason) {
        case ChangesetReason.modified:
          if (index !== -1) {
            appLinks[index] = link;
          }
          break;
        case ChangesetReason.added:
          if (index === -1) {
            appLinks.push(link);
          }
          break;
        case ChangesetReason.deleted:
          if (index !== -1) {
            appLinks.splice(index, 1);
          }
          break;
      }
    });

    // Merge module local variables.
    (moduleChangeset.localVariables || []).forEach((changeset) => {
      const appLocalVariables = appModule.localVariables ?? [];
      const localVariable = changeset.object;
      const index = appLocalVariables.findIndex((lv) => {
        return lv.id === localVariable.id;
      });

      switch (changeset.reason) {
        case ChangesetReason.modified:
          if (index !== -1) {
            appLocalVariables[index] = localVariable;
          }
          break;
        case ChangesetReason.added:
          if (index === -1) {
            appLocalVariables.push(localVariable);
            window.apm.addModuleInput(localVariable, key, design);
          }
          break;
        case ChangesetReason.deleted:
          if (index !== -1) {
            appLocalVariables.splice(index, 1);
            window.apm.deleteModuleInput(localVariable, key, design);
          }
          break;
      }
    });
  });

  // Merge functions.
  Object.entries(
    changeMap.functions ?? {}
  ).forEach(([key, changeset]) => {
    switch (changeset.reason) {
      case ChangesetReason.modified:
        design.functions = design.functions.map((func) => {
          return (func.id === key) ? changeset.object : func;
        });
        break;
      case ChangesetReason.added:
        design.functions.push(changeset.object);
        break;
      case ChangesetReason.deleted:
        design.functions = design.functions.filter((func) => {
          return func.id !== key;
        });
        break;
    }
  });

  // Merge globalIntents.
  Object.entries(
    changeMap.globalIntents ?? {}
  ).forEach(([key, changeset]) => {
    switch (changeset.reason) {
      case ChangesetReason.modified:
        design.globalIntents = design.globalIntents.map((gi) => {
          return (gi.id === key) ? changeset.object : gi;
        });
        break;
      case ChangesetReason.added:
        design.globalIntents.push(changeset.object);
        break;
      case ChangesetReason.deleted:
        design.globalIntents = design.globalIntents.filter((gi) => {
          return gi.id !== key;
        });
        break;
    }
  });

  // Merge compoundIntents.
  Object.entries(
    changeMap.compoundIntents ?? {}
  ).forEach(([key, changeset]) => {
    switch (changeset.reason) {
      case ChangesetReason.modified:
        design.compoundIntents = design.compoundIntents.map((ci) => {
          return (ci.id === key) ? changeset.object : ci;
        });
        break;
      case ChangesetReason.added:
        design.compoundIntents.push(changeset.object);
        break;
      case ChangesetReason.deleted:
        design.compoundIntents = design.compoundIntents.filter((ci) => {
          return ci.id !== key;
        });
        break;
    }
  });

  // Merge filler prompts.
  (Object.entries(
    changeMap.fillerPrompts ?? {}
  )).forEach(([untypedPromptsKey, promptsChangeset]) => {
    const promptsKey = untypedPromptsKey as keyof(Required<DesignChangeMap>['fillerPrompts']);

    Object.entries(
      promptsChangeset
    ).forEach(([key, changeset]) => {
      switch (changeset.reason) {
        case ChangesetReason.modified:
          design.fillerPrompts[promptsKey] = design.fillerPrompts[promptsKey].map((p) => {
            return (p.name === key) ? changeset.object : p;
          });
          break;
      }
    });
  });

  // Merge graphQLSchemas.
  Object.entries(
    changeMap.graphQLSchemas ?? {}
  ).forEach(([key, changeset]) => {
    switch (changeset.reason) {
      case ChangesetReason.modified:
        design.graphQLSchemas = design.graphQLSchemas.map((schema) => {
          return (schema.id === key) ? changeset.object : schema;
        });
        break;
      case ChangesetReason.added:
        design.graphQLSchemas.push(changeset.object);
        break;
      case ChangesetReason.deleted:
        design.graphQLSchemas = design.graphQLSchemas.filter((schema) => {
          return schema.id !== key;
        });
        break;
    }
  });

  // Merge csvData.
  Object.entries(
    changeMap.csvData ?? {}
  ).forEach(([key, changeset]) => {
    switch (changeset.reason) {
      case ChangesetReason.modified:
        design.csvData = design.csvData.map((csvData) => {
          return (csvData.id === key) ? changeset.object : csvData;
        });
        break;
      case ChangesetReason.added:
        design.csvData.push(changeset.object);
        break;
      case ChangesetReason.deleted:
        design.csvData = design.csvData.filter((csvData) => {
          return csvData.id !== key;
        });
        break;
    }
  });

  // Merge ccxml.
  Object.entries(
    changeMap.ccxml ?? {}
  ).forEach(([_, changeset]) => {
    switch (changeset.reason) {
      case ChangesetReason.modified:
        design.ccxml = changeset.object;
    }
  });

  if (changeMap.intentHandling) {
    design.intentHandling = changeMap.intentHandling.object;
  }

  // Merge settings.
  (Object.entries(
    changeMap.settings ?? {}
  ) as [keyof Required<DesignChangeMap>['settings'], unknown][]).forEach(([key, changeset]) => {
    // TODO: Settings are not checked for confilcts.
    if (key === 'description') {
      const typedChangeset = (changeset as ChangeMapSetting<typeof key>);
      design[key] = typedChangeset.object;
      design.descriptionLastLocalChange = (typedChangeset.lastChange) ? (
        typedChangeset.lastChange
      ) : design.descriptionLastLocalChange;
    } else if (key === 'globalErrorHandling') {
      design[key] = (changeset as ChangeMapSetting<typeof key>).object;
    } else if (key === 'globalExceptionHandling') {
      design[key] = (changeset as ChangeMapSetting<typeof key>).object;
    } else if (key === 'intentHandling') {
      const typeChangeset = (changeset as ChangeMapSetting<typeof key>);
      if (typeChangeset.inARowLimit?.lastChange) {
        design[key]!.inARowLimit = typeChangeset.inARowLimit.object;
        design[key]!.inARowLimitLastLocalChange = typeChangeset.inARowLimit.lastChange;
      }
      if (typeChangeset.xAndOutLimit?.lastChange) {
        design[key]!.xAndOutLimit = typeChangeset.xAndOutLimit.object;
        design[key]!.xAndOutLimitLastLocalChange = typeChangeset.xAndOutLimit.lastChange;
      }
    } else if (key === 'omniChannel') {
      design[key] = (changeset as ChangeMapSetting<typeof key>).object;
    } else if (key === 'sensitiveOverrides') {
      design[key] = (changeset as ChangeMapSetting<typeof key>).object;
    } else if (key === 'noInputTimeout') {
      const typedChangeset = (changeset as ChangeMapSetting<typeof key>);
      design[key] = typedChangeset.object;
      design.noInputTimeoutLastLocalChange = (typedChangeset.lastChange) ? (
        typedChangeset.lastChange
      ) : design.noInputTimeoutLastLocalChange;
    } else if (key === 'dtmf_live_agent_override_pattern') {
      design[key] = (changeset as ChangeMapSetting<typeof key>).object;
    } else if (key === 'globalLoopHandling') {
      if (!design[key]) {
        return;
      }

      const globalLoopHandlingPrevChangeMap = (changeset as ChangeMapSetting<typeof key>);
      if (globalLoopHandlingPrevChangeMap.inARow) {
        design[key]!.inARow = globalLoopHandlingPrevChangeMap.inARow.object;
      }

      if (globalLoopHandlingPrevChangeMap.xAndOut) {
        design[key]!.xAndOut = globalLoopHandlingPrevChangeMap.xAndOut.object;
      }
    } else if (key === 'rootSettings') {
      const rootSettingsChangeMap = (changeset as DesignRootSettingsChangeItem);
      design.rootSettings = {
        ...design.rootSettings,
        ...rootSettingsChangeMap.object
      };
    } else {
      // `never` type enforces all keys being handled.
      const neverKey: never = key;
      errors.push(`Unexpected key '${neverKey}' on DesignChangeMap.settings`);
      return;
    }
  });

  if (errors.length) {
    const errorString = errors.join('\n');
    errorMessageService.addError({
      message: errorString,
      title: `${errors.length} fatal errors occurred while applying changes to the Design`
    });
    throw new Error(errorString);
  }

  // Merge languages.
  switch (changeMap.languages?.reason) {
    case ChangesetReason.modified:
      design = {
        ...design,
        ...changeMap.languages.object
      };
      break;
  }

  // Merge app properties.
  Object.entries(
    changeMap.appProperties ?? {}
  ).forEach(([key, changeset]) => {
    design.appProperties ??= [];
    switch (changeset.reason) {
      case ChangesetReason.modified:
        design.appProperties = design.appProperties.map((appProp) => {
          return (appProp.config === key || appProp.id === key) ? (
            changeset.object
          ) : appProp;
        });
        break;
      case ChangesetReason.added:
        design.appProperties.push(changeset.object);
        break;
      case ChangesetReason.deleted:
        design.appProperties = design.appProperties.filter((appProp) => {
          // older designs may use config instead of id.
          return (
            appProp.config !== key
            && appProp.id !== key
          );

        });
        break;
    }
  });

  // Merge unit tests.
  Object.entries(
    changeMap.unitTests ?? {}
  ).forEach(([key, changeset]) => {
    design.unitTests ??= [];
    switch (changeset.reason) {
      case ChangesetReason.modified:
        design.unitTests = design.unitTests.map((unitTest) => {
          return (unitTest.id === key) ? changeset.object : unitTest;
        });
        break;
      case ChangesetReason.added:
        design.unitTests.push(changeset.object);
        break;
      case ChangesetReason.deleted:
        design.unitTests = design.unitTests.filter((unitTest) => {
          return unitTest.id !== key;
        });
        break;
    }
  });

  // Merge sample dialogs.
  Object.entries(
    changeMap.dialogs ?? {}
  ).forEach(([key, changeset]) => {
    switch (changeset.reason) {
      case ChangesetReason.modified:
        design.dialogs = design.dialogs.map((dialog) => {
          return (dialog.id === key) ? changeset.object : dialog;
        });
        break;
      case ChangesetReason.added:
        design.dialogs.push(changeset.object);
        break;
      case ChangesetReason.deleted:
        design.dialogs = design.dialogs.filter((dialog) => {
          return dialog.id !== key;
        });
        break;
    }
  });

  // Merge folders.
  Object.entries(
    changeMap.folders ?? {}
  ).forEach(([key, changeset]) => {
    design.folders ??= { modules: [] };
    switch (changeset.reason) {
      case ChangesetReason.modified:
        design.folders.modules = design.folders.modules.map((folder) => {
          return (folder.id === key) ? changeset.object : folder;
        });
        break;
      case ChangesetReason.added:
        design.folders.modules.push(changeset.object);
        break;
      case ChangesetReason.deleted:
        design.folders.modules = design.folders.modules.filter((folder) => {
          return folder.id !== key;
        });
        break;
    }
  });

  return design;
}
